import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    CheckCircle, AlertCircle, FileText, Calendar,
    Building2, User, Check, Loader2, ChevronLeft
} from 'lucide-react';
import { useContractsStore } from '../../store/contractsStore';
import { Contract } from '../../services/contracts.service';

const SignContractPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isViewMode = searchParams.get('view') === 'true';
    const { contracts, fetchContracts, signByCreator, isLoading } = useContractsStore();
    const [contract, setContract] = useState<Contract | null>(null);

    // Signature form state
    const [name, setName] = useState('');
    const [title, setTitle] = useState('');
    const [signatureType, setSignatureType] = useState<'type' | 'draw'>('type');
    const [typedSignature, setTypedSignature] = useState('');
    const [consent, setConsent] = useState(false);
    const [isSigning, setIsSigning] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Canvas ref for drawing signature
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    useEffect(() => {
        if (contracts.length === 0) {
            fetchContracts();
        }
    }, [fetchContracts, contracts.length]);

    useEffect(() => {
        if (id && contracts.length > 0) {
            const found = contracts.find(c => c.id === id);
            setContract(found || null);
        }
    }, [id, contracts]);

    // Canvas drawing handlers
    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        setIsDrawing(true);
        const rect = canvas.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const getSignatureData = (): string => {
        if (signatureType === 'type') {
            return typedSignature;
        } else {
            const canvas = canvasRef.current;
            if (!canvas) return '';
            return canvas.toDataURL('image/png');
        }
    };

    const handleSign = async () => {
        if (!contract) return;

        const signatureData = getSignatureData();
        if (!signatureData || !name || !consent) {
            setError('Please fill in all required fields and accept the agreement.');
            return;
        }

        setIsSigning(true);
        setError(null);
        try {
            await signByCreator(contract.id, signatureData);
            navigate('/dashboard/contracts');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to sign contract');
        } finally {
            setIsSigning(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    // Loading state
    if (isLoading || !contract) {
        return (
            <div className="min-h-[133.33vh] bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
                    <p className="text-slate-600 dark:text-gray-400">Loading contract...</p>
                </div>
            </div>
        );
    }

    // Error state - contract not found
    if (!contract) {
        return (
            <div className="min-h-[133.33vh] bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Contract Not Found</h1>
                    <p className="text-slate-600 dark:text-gray-400 mb-6">
                        The contract you're looking for doesn't exist.
                    </p>
                    <button
                        onClick={() => navigate('/dashboard/contracts')}
                        className="px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                    >
                        Return to Contracts
                    </button>
                </div>
            </div>
        );
    }

    // Already signed state (only show if NOT in view mode)
    if (contract.signatures?.creatorSignedAt && !isViewMode) {
        return (
            <div className="min-h-[133.33vh] bg-gradient-to-br from-green-50 to-emerald-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">You've Already Signed!</h1>
                    <p className="text-slate-600 dark:text-gray-400 mb-6">
                        {contract.status === 'active'
                            ? 'Both parties have signed. The contract is now active.'
                            : 'Awaiting signature from the other party.'}
                    </p>
                    <button
                        onClick={() => navigate('/dashboard/contracts')}
                        className="px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                    >
                        Return to Contracts
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[133.33vh] bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 py-8 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/dashboard/contracts')}
                    className="flex items-center text-sm text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors"
                >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back to Contracts
                </button>

                {/* Header */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <FileText className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                        {isViewMode ? 'Contract Agreement' : 'Sign Contract'}
                    </h1>
                    <p className="text-slate-600 dark:text-gray-400">
                        {isViewMode ? 'View the signed contract details' : 'Please review and sign the contract below'}
                    </p>
                </div>

                {/* Brand Signed Notice */}
                {contract.signatures?.brandSignedAt && !contract.signatures?.creatorSignedAt && !isViewMode && (
                    <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6">
                        <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <span className="text-blue-700 dark:text-blue-300 text-sm font-medium">
                            The brand has already signed. Your signature is needed to activate the contract.
                        </span>
                    </div>
                )}

                {/* View Mode: Signature Status */}
                {isViewMode && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 mb-6">
                        <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium mb-3">
                            <CheckCircle className="w-5 h-5" />
                            Contract Fully Signed
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="text-slate-500 dark:text-gray-400">Creator Signed</p>
                                <p className="font-medium text-slate-900 dark:text-white">
                                    {contract.signatures?.creatorSignedAt ? new Date(contract.signatures.creatorSignedAt).toLocaleDateString() : 'Pending'}
                                </p>
                            </div>
                            <div>
                                <p className="text-slate-500 dark:text-gray-400">Brand Signed</p>
                                <p className="font-medium text-slate-900 dark:text-white">
                                    {contract.signatures?.brandSignedAt ? new Date(contract.signatures.brandSignedAt).toLocaleDateString() : 'Pending'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Contract Details */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden mb-6">
                    {/* Contract Header */}
                    <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6">
                        <h2 className="text-xl font-semibold mb-2">{contract.title}</h2>
                        <div className="flex items-center gap-4 text-primary-100 text-sm">
                            <span className="flex items-center gap-1">
                                <Building2 className="w-4 h-4" />
                                {contract.partnership?.brand || 'Brand'}
                            </span>
                            <span className="flex items-center gap-1">
                                {formatCurrency(contract.totalValue)}
                            </span>
                        </div>
                    </div>

                    {/* Contract Body */}
                    <div className="p-6 space-y-6">
                        {/* Dates */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-slate-50 dark:bg-gray-700/50 rounded-xl p-4">
                                <div className="flex items-center gap-2 text-slate-500 dark:text-gray-400 text-sm mb-1">
                                    <Calendar className="w-4 h-4" />
                                    Start Date
                                </div>
                                <p className="font-medium text-slate-900 dark:text-white">{formatDate(contract.startDate)}</p>
                            </div>
                            <div className="bg-slate-50 dark:bg-gray-700/50 rounded-xl p-4">
                                <div className="flex items-center gap-2 text-slate-500 dark:text-gray-400 text-sm mb-1">
                                    <Calendar className="w-4 h-4" />
                                    End Date
                                </div>
                                <p className="font-medium text-slate-900 dark:text-white">{formatDate(contract.endDate)}</p>
                            </div>
                        </div>

                        {/* Deliverables */}
                        {contract.deliverables && contract.deliverables.length > 0 && (
                            <div>
                                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Deliverables</h3>
                                <ul className="space-y-2">
                                    {contract.deliverables.map((del, idx) => (
                                        <li key={idx} className="flex items-start gap-3 bg-slate-50 dark:bg-gray-700/50 rounded-lg p-3">
                                            <Check className="w-5 h-5 text-green-500 mt-0.5" />
                                            <div>
                                                <p className="font-medium text-slate-900 dark:text-white">{del.title}</p>
                                                {del.description && (
                                                    <p className="text-sm text-slate-500 dark:text-gray-400">{del.description}</p>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Payment Terms */}
                        {contract.paymentTerms && (
                            <div>
                                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Payment Terms</h3>
                                <div className="bg-slate-50 dark:bg-gray-700/50 rounded-xl p-4">
                                    <p className="text-slate-600 dark:text-gray-300 capitalize">{contract.paymentTerms.type}</p>
                                    {contract.paymentTerms.milestones && contract.paymentTerms.milestones.length > 0 && (
                                        <ul className="mt-3 space-y-2">
                                            {contract.paymentTerms.milestones.map((m, idx) => (
                                                <li key={idx} className="flex justify-between text-sm">
                                                    <span className="text-slate-600 dark:text-gray-400">{m.name}</span>
                                                    <span className="font-medium text-slate-900 dark:text-white">{m.percentage}%</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        {contract.notes && (
                            <div>
                                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Additional Notes</h3>
                                <p className="text-slate-600 dark:text-gray-300 bg-slate-50 dark:bg-gray-700/50 rounded-xl p-4">{contract.notes}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Signature Section - Only show when NOT in view mode */}
                {!isViewMode && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6">Your Signature</h3>

                        {/* Name & Title */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
                                    <User className="w-4 h-4 inline mr-1" />
                                    Full Name *
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full px-4 py-2 border border-slate-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-slate-900 dark:text-white"
                                    placeholder="Your full name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
                                    <Building2 className="w-4 h-4 inline mr-1" />
                                    Title (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="w-full px-4 py-2 border border-slate-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-slate-900 dark:text-white"
                                    placeholder="Content Creator"
                                />
                            </div>
                        </div>

                        {/* Signature Type Toggle */}
                        <div className="flex gap-2 mb-4">
                            <button
                                onClick={() => setSignatureType('type')}
                                className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${signatureType === 'type'
                                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border-2 border-primary-500'
                                    : 'bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-gray-400 border-2 border-transparent'
                                    }`}
                            >
                                Type Signature
                            </button>
                            <button
                                onClick={() => setSignatureType('draw')}
                                className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${signatureType === 'draw'
                                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border-2 border-primary-500'
                                    : 'bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-gray-400 border-2 border-transparent'
                                    }`}
                            >
                                Draw Signature
                            </button>
                        </div>

                        {/* Signature Input */}
                        {signatureType === 'type' ? (
                            <div className="mb-6">
                                <input
                                    type="text"
                                    value={typedSignature}
                                    onChange={(e) => setTypedSignature(e.target.value)}
                                    className="w-full px-4 py-4 border-2 border-dashed border-slate-300 dark:border-gray-600 rounded-xl text-center text-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-slate-900 dark:text-white"
                                    placeholder="Type your signature"
                                    style={{ fontFamily: "'Dancing Script', cursive" }}
                                />
                            </div>
                        ) : (
                            <div className="mb-6">
                                <div className="relative border-2 border-dashed border-slate-300 dark:border-gray-600 rounded-xl overflow-hidden">
                                    <canvas
                                        ref={canvasRef}
                                        width={500}
                                        height={150}
                                        className="w-full bg-white dark:bg-gray-700 cursor-crosshair"
                                        onMouseDown={startDrawing}
                                        onMouseMove={draw}
                                        onMouseUp={stopDrawing}
                                        onMouseLeave={stopDrawing}
                                    />
                                    <button
                                        onClick={clearCanvas}
                                        className="absolute top-2 right-2 text-sm text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Consent Checkbox */}
                        <label className="flex items-start gap-3 mb-6 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={consent}
                                onChange={(e) => setConsent(e.target.checked)}
                                className="mt-1 w-5 h-5 text-primary-600 border-slate-300 dark:border-gray-600 rounded focus:ring-primary-500"
                            />
                            <span className="text-sm text-slate-600 dark:text-gray-400">
                                I agree to use electronic records and signatures, and I understand that by signing this contract,
                                I am entering into a legally binding agreement.
                            </span>
                        </label>

                        {/* Error Message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm">
                                {error}
                            </div>
                        )}

                        {/* Sign Button */}
                        <button
                            onClick={handleSign}
                            disabled={isSigning || !name || !consent || (!typedSignature && signatureType === 'type')}
                            className="w-full py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                        >
                            {isSigning ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Signing...
                                </>
                            ) : (
                                <>
                                    <Check className="w-5 h-5" />
                                    Sign Contract
                                </>
                            )}
                        </button>
                    </div>
                )}

                {/* View Mode: Signature Info */}
                {isViewMode && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6">Signatures</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Creator Signature */}
                            <div className="bg-slate-50 dark:bg-gray-700/50 rounded-xl p-4 flex flex-col min-h-[140px]">
                                <p className="text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Creator Signature</p>
                                {contract.signatures?.creatorSignedAt ? (
                                    <>
                                        <div className="flex-1">
                                            {contract.signatures.creatorSignature && (
                                                contract.signatures.creatorSignature.startsWith('data:image') ? (
                                                    <img
                                                        src={contract.signatures.creatorSignature}
                                                        alt="Creator Signature"
                                                        className="max-h-16"
                                                    />
                                                ) : (
                                                    <p className="text-2xl text-slate-900 dark:text-white" style={{ fontFamily: "'Dancing Script', cursive" }}>
                                                        {contract.signatures.creatorSignature}
                                                    </p>
                                                )
                                            )}
                                        </div>
                                        <p className="text-sm text-green-600 dark:text-green-400 mt-4">
                                            ✓ Signed on {new Date(contract.signatures.creatorSignedAt).toLocaleDateString()}
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-slate-500 dark:text-gray-400">Pending</p>
                                )}
                            </div>

                            {/* Brand Signature */}
                            <div className="bg-slate-50 dark:bg-gray-700/50 rounded-xl p-4 flex flex-col min-h-[140px]">
                                <p className="text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Brand Signature</p>
                                {contract.signatures?.brandSignedAt ? (
                                    <>
                                        <div className="flex-1">
                                            {contract.signatures.brandSignature && (
                                                contract.signatures.brandSignature.startsWith('data:image') ? (
                                                    <img
                                                        src={contract.signatures.brandSignature}
                                                        alt="Brand Signature"
                                                        className="max-h-16"
                                                    />
                                                ) : (
                                                    <p className="text-2xl text-slate-900 dark:text-white" style={{ fontFamily: "'Dancing Script', cursive" }}>
                                                        {contract.signatures.brandSignature}
                                                    </p>
                                                )
                                            )}
                                        </div>
                                        <p className="text-sm text-green-600 dark:text-green-400 mt-4">
                                            ✓ Signed on {new Date(contract.signatures.brandSignedAt).toLocaleDateString()}
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-slate-500 dark:text-gray-400">Pending</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <p className="text-center text-sm text-slate-500 dark:text-gray-500 mt-6">
                    {isViewMode ? 'This is a legally binding agreement.' : 'This is a secure signing page. Your signature will be legally binding.'}
                </p>
            </div>
        </div>
    );
};

export default SignContractPage;
